import { type ReactNode, type CSSProperties, useEffect, useCallback } from "react";
import { Outlet, useLocation, useNavigate } from "react-router";
import { TopBar } from "./TopBar";
import { ScopeRail } from "./ScopeRail";
import { RightRailProvider } from "./RightRailContext";
import { ChatRail } from "../chat/ChatRail";
import { ChatRailProvider, useChatRail } from "../chat/ChatRailContext";
import { CommandPaletteProvider, useCommandPalette } from "../command-palette/CommandPaletteContext";
import { CommandPalette } from "../command-palette/CommandPalette";
import { RowDetailProvider } from "../entity/RowDetailContext";
import { RowDetailSidePanel } from "../entity/RowDetailSidePanel";
import { SessionScopeProvider } from "../../hooks/use-session-scope";
import { ToastContainer } from "../primitives";
import { PickupToastObserver } from "../../hooks/use-pickup-toast";

interface AppShellProps {
  children?: ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  return (
    <SessionScopeProvider>
      <ChatRailProvider>
        <RightRailProvider>
          <RowDetailProvider>
            <CommandPaletteProvider>
              <AppShellLayout>{children}</AppShellLayout>
            </CommandPaletteProvider>
          </RowDetailProvider>
        </RightRailProvider>
      </ChatRailProvider>
    </SessionScopeProvider>
  );
}

function AppShellLayout({ children }: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { expanded, toggleExpanded } = useChatRail();
  const { open: paletteOpen, setOpen: setPaletteOpen, openCheatsheet } = useCommandPalette();

  // Sprint 22 — the third column is now the cockpit chat, available on every
  // route. 380px when expanded (chat needs more room than the old file lists),
  // a slim 40px strip when collapsed. The `]` shortcut toggles it. (Cockpit's
  // former right-rail content — files + activity — moved into the main column.)
  // Sprint 23.5 — the Hire wizard route collapses the right column entirely
  // (ChatRail returns null on /hire); skip reserving the 380/40px slot too.
  const onWizard = location.pathname === "/hire";
  const rightColumn = onWizard ? "0px" : expanded ? "380px" : "40px";

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      // Cmd/Ctrl+K — toggle the command palette (works regardless of focus).
      if (meta && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        if (paletteOpen) {
          setPaletteOpen(false);
        } else if (!document.querySelector('[role="dialog"]')) {
          // Modal isolation (S3.41): don't steal focus from an open modal.
          setPaletteOpen(true);
        }
        return;
      }
      // Cmd/Ctrl+/ — open the palette directly in cheatsheet mode.
      if (meta && e.key === "/") {
        e.preventDefault();
        if (paletteOpen || !document.querySelector('[role="dialog"]')) {
          openCheatsheet();
        }
        return;
      }
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      if ((e.target as HTMLElement)?.isContentEditable) return;
      if (e.key === "n") {
        // Sprint 23.5 — `n` now opens the Hire wizard (conversational
        // composition) instead of the old NewAgentModal.
        e.preventDefault();
        navigate("/hire");
      } else if (e.key === "]") {
        e.preventDefault();
        toggleExpanded();
      }
    },
    [toggleExpanded, paletteOpen, setPaletteOpen, openCheatsheet, navigate],
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  const root: CSSProperties = {
    display: "grid",
    gridTemplateRows: "48px 1fr",
    gridTemplateColumns: `240px 1fr ${rightColumn}`,
    height: "100vh",
    width: "100vw",
    overflow: "hidden",
    background: "var(--color-background-tertiary)",
  };

  const topBarArea: CSSProperties = {
    gridColumn: "1 / -1",
    gridRow: "1",
  };

  const railArea: CSSProperties = {
    gridColumn: "1",
    gridRow: "2",
    overflow: "hidden",
  };

  const mainArea: CSSProperties = {
    gridColumn: "2",
    gridRow: "2",
    overflow: "auto",
    padding: "24px 32px",
  };

  const rightRailArea: CSSProperties = {
    gridColumn: "3",
    gridRow: "2",
    overflow: "hidden",
  };

  return (
    <div data-v3 style={root}>
      <div style={topBarArea}>
        <TopBar />
      </div>
      <div style={railArea}>
        <ScopeRail />
      </div>
      <main style={mainArea}>
        {children ?? <Outlet />}
      </main>
      <div style={rightRailArea}>
        <ChatRail />
      </div>
      <CommandPalette />
      <RowDetailSidePanel />
      <ToastContainer />
      {/* Sprint 24.17a — surfaces the Architect pickup toast from the activity feed. */}
      <PickupToastObserver />
    </div>
  );
}
